import cloudflareLoader from "@/lib/cloudflare-images";
import clsx from "clsx";
import Target from "components/Target";
import Image from "next/legacy/image";
import { useMemo, useState } from "react";
import { ArrowUpRight, MinusCircle, PlusCircle } from "react-feather";

const RecipeCTA = ({
  heading,
  headingImage = null,
  onCTAClick = () => {},
  content,
  backgroundColor = "#f4f4f5",
  color = "#14532d",
  target,
  image = null,
  findOutMoreText = "Find out more",
}) => {
  const [isShowMoreActive, setIsShowMoreActive] = useState(false);

  // Transform the backgroundColor from #ffffff to something else
  if (backgroundColor === "#ffffff") {
    backgroundColor = "#f4f4f5";
  }

  const isLightBackground = (color: string) => {
    const hex = color.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 160;
  };

  const borderColor = useMemo(() => {
    return isLightBackground(backgroundColor)
      ? "rgba(0, 0, 0, 0.05)"
      : "rgba(255, 255, 255, 0.25)";
  }, [backgroundColor]);

  // We need to identify if this is a deliveroo cta, so we can adjust the padding somewhat.
  // TODO: remove this once the deliveroo promo is no longer used
  const isDeliveroo = image?.url.includes("Deliveroo-ODG-Image");

  return (
    <div className={clsx("RecipeCTA", "@container")}>
      <div
        className={clsx("relative overflow-hidden lg:rounded-2xl")}
        style={{ backgroundColor, color }}
      >
        <div className={clsx("flex")}>
          {!!image && (
            <div
              className={clsx("relative order-2", {
                "w-full": !image,
                "py-3": isDeliveroo,
              })}
            >
              <div
                className={clsx("relative aspect-square w-32 sm:w-40 md:w-40")}
              >
                {!!image && (
                  <Image
                    src={image.url}
                    layout="fill"
                    objectFit="cover"
                    alt={image.title}
                    loader={cloudflareLoader}
                    sizes="(max-width: 768px) 25vw, 160px"
                  />
                )}
              </div>
            </div>
          )}
          <div
            className={clsx("order-1 flex-1", {
              "w-full": !image,
            })}
          >
            <div
              className={clsx(
                "flex h-full flex-col space-y-3 pb-6 pt-6 @lg:pb-8 @lg:pt-8"
              )}
            >
              <h3
                className={clsx(
                  "px-6 font-bold @lg:px-8 @lg:text-lg @xl:text-xl"
                )}
              >
                {!headingImage && <span>{heading}</span>}
                {!!headingImage && (
                  <div
                    className={clsx(
                      "relative aspect-[16/1.5] @lg:aspect-[16/2] @xl:aspect-[16/1]"
                    )}
                  >
                    <Image
                      src={headingImage}
                      alt={heading}
                      layout="fill"
                      objectPosition={"0% 50%"}
                      objectFit="contain"
                      loader={cloudflareLoader}
                    />
                  </div>
                )}
              </h3>
              <div
                className={clsx(
                  "body-text-sm hidden max-w-prose px-6 @lg:block @lg:px-8 [&_p]:leading-normal"
                )}
                dangerouslySetInnerHTML={{ __html: content }}
              ></div>
              <div className={clsx("mt-auto px-6 @lg:hidden @lg:px-8")}>
                <button
                  className={clsx("flex items-center space-x-2 text-sm")}
                  onClick={() => setIsShowMoreActive(!isShowMoreActive)}
                >
                  <div className={clsx("font-body")}>{findOutMoreText}</div>
                  <div>
                    {isShowMoreActive ? (
                      <MinusCircle size={16} />
                    ) : (
                      <PlusCircle size={16} />
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className={clsx("body-text-sm border-t px-6 py-6 @lg:px-8 @lg:py-8", {
            hidden: !isShowMoreActive,
            "block @lg:hidden": isShowMoreActive,
          })}
          style={{ borderColor }}
          dangerouslySetInnerHTML={{ __html: content }}
        ></div>

        <div className={clsx("RecipeCTA__footer")}>
          <Target
            target={target}
            onClick={() => {
              onCTAClick();

              // TODO: This is gross and should be removed once the jet2 competition is over
              // this adds a trigger for a klaviyo popup
              if (heading?.includes("Jet2")) {
                window._klOnsite = window._klOnsite || [];
                window._klOnsite.push(["openForm", "ST7ZWH"]);
              }
            }}
            className={clsx(
              "group flex items-center border-t border-white/25 px-6 py-3 transition-colors @lg:px-8 @lg:py-5 hover:bg-zinc-800/[.0125] "
            )}
            style={{ borderColor }}
          >
            <div className={clsx("font-body text-sm")}>{target.text}</div>
            <div
              className={clsx(
                "ml-auto h-5 w-5 transition group-hover:-translate-y-1 group-hover:translate-x-1"
              )}
            >
              <ArrowUpRight strokeWidth={1.5} size={"auto"} />
            </div>
          </Target>
        </div>
      </div>
    </div>
  );
};

export default RecipeCTA;
